import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { createInvitation, resendInvitation } from './FormikHelpers';
import jwt_decode from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../../../store/actions';

const personTypes = [
  { value: 'employee', label: 'Employee' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

const QUICK_DAYS = ['3', '7', '14'];

const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap');

  .inv-modal-overlay .modal-dialog {
    max-width: 640px;
  }

  .inv-modal-overlay .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08);
    font-family: 'Geist', 'DM Sans', sans-serif;
    overflow: hidden;
  }

  .inv-header {
    background: #0a1628;
    padding: 20px 24px 16px;
    border-bottom: none;
    position: relative;
    overflow: hidden;
  }

  .inv-header::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 90% 50%, rgba(0,200,130,0.12) 0%, transparent 60%);
    pointer-events: none;
  }

  .inv-header .modal-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .inv-header-icon {
    width: 34px; height: 34px;
    border-radius: 9px;
    background: rgba(0,200,130,0.15);
    border: 1px solid rgba(0,200,130,0.25);
    display: flex; align-items: center; justify-content: center;
    color: #00c882;
    flex-shrink: 0;
  }

  .inv-header-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
    margin: 0;
  }

  .inv-header-sub {
    font-size: 11.5px;
    color: rgba(255,255,255,0.45);
    margin: 0;
    margin-top: 1px;
  }

  .inv-header-close {
    margin-left: auto;
    background: rgba(255, 255, 255, 0.12);
    border: none;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.18s;
  }

  .inv-header-close:hover,
  .inv-header-close:focus {
    background: rgba(255, 255, 255, 0.28);
    transform: scale(1.1);
  }

  .inv-header-close:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
  }

  .inv-context {
    background: #f4f6f9;
    border-bottom: 1px solid #e8eaed;
    padding: 8px 24px;
    display: flex;
    gap: 24px;
  }

  .inv-ctx-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .inv-ctx-icon { color: #9aa3ae; }

  .inv-ctx-label {
    font-size: 11px;
    font-weight: 500;
    color: #9aa3ae;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .inv-ctx-val {
    font-size: 12px;
    font-weight: 600;
    color: #2d3748;
    margin-left: 2px;
  }

  .inv-body {
    padding: 20px 24px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .inv-sec-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .inv-sec-label {
    font-size: 11.5px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0;
    white-space: nowrap;
  }

  .inv-sec-rule {
    flex: 1;
    height: 1px;
    background: #e8eaed;
  }

  .inv-person-card {
    background: #f9fafb;
    border: 1px solid #e8eaed;
    border-radius: 12px;
    padding: 14px 16px 10px;
    margin-bottom: 10px;
    position: relative;
  }

  .inv-person-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 14px;
  }

  .inv-person-grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px 14px;
  }

  @media (max-width: 520px) {
    .inv-person-grid-2,
    .inv-person-grid-3 {
      grid-template-columns: 1fr;
    }
  }

  .inv-person-remove {
    position: absolute;
    top: 10px; right: 12px;
    width: 26px; height: 26px;
    border-radius: 7px;
    border: 1px solid #fbd5d5;
    background: #fff5f5;
    color: #e53e3e;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 0.12s;
    padding: 0;
  }

  .inv-person-remove:hover { background: #fbd5d5; }

  .inv-person-num {
    font-size: 10.5px;
    font-weight: 700;
    color: #9aa3ae;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .inv-optional-badge {
    font-size: 10px;
    font-weight: 500;
    color: #9aa3ae;
    background: #f0f2f5;
    border-radius: 4px;
    padding: 1px 5px;
    margin-left: 4px;
    letter-spacing: 0.3px;
  }

  .inv-fg { margin-bottom: 0; }

  .inv-label {
    font-size: 11.5px;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .inv-req { color: #e53e3e; }

  .inv-input {
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 12.5px !important;
    color: #1a2a3a !important;
    padding: 7px 10px !important;
    height: 34px !important;
    transition: border-color 0.12s, box-shadow 0.12s !important;
    background: #fff !important;
  }

  .inv-input:focus {
    border-color: #00895e !important;
    box-shadow: 0 0 0 3px rgba(0,137,94,0.1) !important;
    outline: none !important;
  }

  .inv-input.error { border-color: #e53e3e !important; }

  .inv-err {
    font-size: 10.5px;
    color: #e53e3e;
    margin-top: 3px;
  }

  .inv-add-btn {
    font-size: 12px;
    font-weight: 500;
    color: #00895e;
    background: none;
    border: 1.5px dashed #b2dfce;
    border-radius: 8px;
    padding: 7px 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: all 0.12s;
    width: 100%;
    justify-content: center;
  }

  .inv-add-btn:hover { background: #f0fdf8; border-color: #00895e; }

  .inv-global-err {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: #e53e3e;
    background: #fff5f5;
    border: 1px solid #fbd5d5;
    border-radius: 8px;
    padding: 8px 12px;
  }

  .inv-toggle-btn {
    background: none;
    border: none;
    padding: 6px 0 2px;
    font-size: 11px;
    color: #9aa3ae;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color 0.12s;
  }

  .inv-toggle-btn:hover { color: #4a5568; }

  .inv-accordion-section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #e2e8f0;
  }

  .inv-accordion-sub-label {
    font-size: 10.5px;
    font-weight: 600;
    color: #9aa3ae;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 8px;
  }

  .inv-specify-type-note {
    font-size: 10.5px;
    color: #718096;
    margin-top: 3px;
  }

  .inv-textarea {
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 13px !important;
    color: #1a2a3a !important;
    padding: 10px 12px !important;
    resize: vertical !important;
    min-height: 110px !important;
    transition: border-color 0.12s, box-shadow 0.12s !important;
    background: #fff !important;
    line-height: 1.5;
  }

  .inv-textarea:focus {
    border-color: #00895e !important;
    box-shadow: 0 0 0 3px rgba(0,137,94,0.1) !important;
    outline: none !important;
  }

  .inv-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .inv-chip {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 500;
    border: 1.5px solid #e2e8f0;
    background: #fff;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.12s;
    min-width: 62px;
    text-align: center;
  }

  .inv-chip:hover { border-color: #00895e; color: #00895e; background: #f0fdf8; }
  .inv-chip.active { background: #00895e; border-color: #00895e; color: #fff; }

  .inv-bottom-grid {
    display: grid;
    grid-template-columns: minmax(160px, 220px) 1fr;
    gap: 24px;
    margin-top: 24px;
  }

  @media (max-width: 640px) {
    .inv-bottom-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .inv-footer {
    padding: 14px 24px;
    background: #f9fafb;
    border-top: 1px solid #e8eaed;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    align-items: center;
  }

  .inv-cancel {
    font-size: 12.5px;
    font-weight: 500;
    color: #4a5568;
    background: #fff;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    padding: 8px 18px;
    cursor: pointer;
    transition: all 0.12s;
  }

  .inv-cancel:hover { background: #f0f2f5; border-color: #a0aab4; }

  .inv-send {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    background: #00895e;
    border: none;
    border-radius: 8px;
    padding: 8px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s;
    box-shadow: 0 1px 4px rgba(0,137,94,0.3);
  }

  .inv-send:hover:not(:disabled) {
    background: #00774f;
    box-shadow: 0 3px 10px rgba(0,137,94,0.35);
    transform: translateY(-1px);
  }

  .inv-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const selectCustomStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#e53e3e' : state.isFocused ? '#00895e' : '#e2e8f0',
    borderWidth: '1.5px',
    borderRadius: '8px',
    fontSize: '12.5px',
    minHeight: '34px',
    height: '34px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(0,137,94,0.12)' : 'none',
    '&:hover': { borderColor: hasError ? '#e53e3e' : '#00895e' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: '34px' }),
  singleValue: (base) => ({ ...base, fontSize: '12.5px', color: '#1a2a3a' }),
  placeholder: (base) => ({ ...base, fontSize: '12.5px', color: '#a0aab4' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12.5px',
    backgroundColor: state.isSelected ? '#00895e' : state.isFocused ? '#f0fdf8' : 'white',
    color: state.isSelected ? '#fff' : '#1a2a3a',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const emptyPerson = () => ({
  type: '',
  specifyType: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
});

const GenerateIncidentInvitationModal = ({
  isOpen,
  toggle,
  initialData = null,
  propertyId,
  buildingName = '',
  propertyTitle = '',
  onSuccess = () => {},
}) => {
  const dispatch = useDispatch();

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const currentUser = getUserFromToken();
  const companyName = currentUser?.company ||
    currentUser?.companyName ||
    currentUser?.organization ||
    'Your Company';

  const isEditMode = !!initialData;

  const defaultNotesTemplate = useMemo(() => 
    `Hi,\n\nPlease fill out the entire incident report and upload any requested documents.\nThank you in advance for your cooperation.\n\nBest regards,\n${companyName}`,
    [companyName]
  );

  const [persons, setPersons] = useState([]);
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [notes, setNotes] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPersons([emptyPerson()]);
      setExpiresInDays('7');
      setNotes('');
      setFormErrors({});
      setIsSubmitting(false);
      return;
    }

    if (isEditMode && initialData) {
      const prefilled = initialData.prefilledPerson || [];
      setPersons(
        prefilled.length > 0
          ? prefilled.map((p) => ({
              type: p.type || '',
              specifyType: p.specifyType || '',
              firstName: p.firstName || '',
              middleName: p.middleName || '',
              lastName: p.lastName || '',
              email: p.email || '',
              isConfirmed: p.isConfirmed || false,
              confirmedAt: p.confirmedAt || null,
            }))
          : [emptyPerson()]
      );

      let days = 7;
      if (initialData.expiresAt) {
        const diffMs = new Date(initialData.expiresAt) - new Date();
        days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      }
      setExpiresInDays(days.toString());
      setNotes(initialData.notes || '');
    } else {
      setPersons([emptyPerson()]);
      setExpiresInDays('7');
      setNotes(defaultNotesTemplate);
    }

    setFormErrors({});
    setIsSubmitting(false);
  }, [isOpen, isEditMode, initialData, defaultNotesTemplate]);

  const clearError = useCallback((...keys) => {
    setFormErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      delete next.global;
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    let validEntries = 0;

    persons.forEach((person, index) => {
      const email = person.email?.trim();
      const firstName = person.firstName?.trim();
      const lastName = person.lastName?.trim();
      const type = person.type;

      if (email || firstName || lastName || type) {
        if (!firstName) errors[`firstName_${index}`] = 'First name is required';
        if (!lastName) errors[`lastName_${index}`] = 'Last name is required';
        if (!email) errors[`email_${index}`] = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errors[`email_${index}`] = 'Invalid email format';
        }
        if (!type) errors[`type_${index}`] = 'Type is required';

        if (type === 'other' && !person.specifyType?.trim()) {
          errors[`specifyType_${index}`] = 'Please specify the type';
        }

        if (email && type && firstName && lastName) validEntries++;
      }
    });

    if (validEntries === 0) {
      errors.global = 'At least one complete recipient is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [persons]);

  const addPerson = () => setPersons((prev) => [...prev, emptyPerson()]);

  const removePerson = (index) => {
    if (persons.length <= 1) return;
    setPersons((prev) => prev.filter((_, i) => i !== index));
    setFormErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!key.endsWith(`_${index}`)) next[key] = prev[key];
      });
      return next;
    });
  };

  const updatePerson = (index, field, value) => {
    setPersons((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
    clearError(`${field}_${index}`);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please correct the errors shown');
      return;
    }

    setIsSubmitting(true);
    dispatch(setLoading(true));

    const payload = {
      propertyId,
      buildingName: buildingName || undefined,
      prefilledPerson: persons
        .filter((p) => p.email?.trim() && p.type && p.firstName?.trim() && p.lastName?.trim())
        .map((p) => ({
          type: p.type,
          ...(p.type === 'other' && p.specifyType?.trim() && { specifyType: p.specifyType.trim() }),
          firstName: p.firstName.trim(),
          middleName: p.middleName?.trim() || undefined,
          lastName: p.lastName.trim(),
          email: p.email.trim().toLowerCase(),
        })),
      expiresInDays: Number(expiresInDays),
      notes: notes.trim() || undefined,
    };

    try {
      let result;
      if (isEditMode) {
        result = await resendInvitation(initialData._id, payload);
        toast.success(`Invitation resent (${result?.referenceId || '—'})`);
      } else {
        result = await createInvitation(payload);
        toast.success(`Invitation created & sent (${result?.referenceId || '—'})`);
      }

      onSuccess(result);
      toggle();
    } catch (err) {
      console.error('Invitation error:', err);
      toast.error(isEditMode ? 'Failed to resend invitation' : 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      <style>{modalStyles}</style>

      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        backdrop="static"
        className="inv-modal-overlay"
      >
        <ModalHeader className="inv-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            <div className="inv-header-icon">
              <FeatherIcon icon={isEditMode ? 'refresh-cw' : 'send'} size={16} />
            </div>
            <div style={{ flex: 1, width: "38vw" }}>
              <h5 className="inv-header-title mb-0">
                {isEditMode ? 'Resend Invitation' : 'New Incident Invitation'}
              </h5>
              <div className="inv-header-sub">
                {isEditMode
                  ? 'Update recipients, expiry or notes and resend'
                  : 'Invite people to submit an incident report'}
              </div>
            </div>
            <button
              type="button"
              className="inv-header-close"
              onClick={toggle}
              aria-label="Close"
            >
              <FeatherIcon icon="x" size={22} />
            </button>
          </div>
        </ModalHeader>

        {(propertyTitle || buildingName) && (
          <div className="inv-context">
            {propertyTitle && (
              <div className="inv-ctx-item">
                <FeatherIcon icon="home" size={13} className="inv-ctx-icon" />
                <span className="inv-ctx-label">Property</span>
                <span className="inv-ctx-val">{propertyTitle}</span>
              </div>
            )}
            {buildingName && (
              <div className="inv-ctx-item">
                <FeatherIcon icon="layers" size={13} className="inv-ctx-icon" />
                <span className="inv-ctx-label">Building</span>
                <span className="inv-ctx-val">{buildingName}</span>
              </div>
            )}
          </div>
        )}

        <ModalBody className="inv-body">
          {/* Recipients Section */}
          <div>
            <div className="inv-sec-head">
              <span className="inv-sec-label">Recipients</span>
              <div className="inv-sec-rule" />
            </div>

            {persons.map((person, idx) => (
              <div key={idx} className="inv-person-card">
                {persons.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span className="inv-person-num">Person {idx + 1}</span>
                    <button
                      type="button"
                      className="inv-person-remove"
                      onClick={() => removePerson(idx)}
                      aria-label="Remove person"
                    >
                      <FeatherIcon icon="x" size={14} />
                    </button>
                  </div>
                )}

                {/* Name Fields */}
                <div className="inv-person-grid-3" style={{ marginBottom: 8 }}>
                  <FormGroup className="inv-fg">
                    <Label className="inv-label">First Name <span className="inv-req">*</span></Label>
                    <Input
                      className={`inv-input${formErrors[`firstName_${idx}`] ? ' error' : ''}`}
                      value={person.firstName || ''}
                      onChange={(e) => updatePerson(idx, 'firstName', e.target.value)}
                      placeholder="Jane"
                    />
                    {formErrors[`firstName_${idx}`] && <div className="inv-err">{formErrors[`firstName_${idx}`]}</div>}
                  </FormGroup>

                  <FormGroup className="inv-fg">
                    <Label className="inv-label">Middle Name <span className="inv-optional-badge">optional</span></Label>
                    <Input
                      className="inv-input"
                      value={person.middleName || ''}
                      onChange={(e) => updatePerson(idx, 'middleName', e.target.value)}
                      placeholder="A."
                    />
                  </FormGroup>

                  <FormGroup className="inv-fg">
                    <Label className="inv-label">Last Name <span className="inv-req">*</span></Label>
                    <Input
                      className={`inv-input${formErrors[`lastName_${idx}`] ? ' error' : ''}`}
                      value={person.lastName || ''}
                      onChange={(e) => updatePerson(idx, 'lastName', e.target.value)}
                      placeholder="Smith"
                    />
                    {formErrors[`lastName_${idx}`] && <div className="inv-err">{formErrors[`lastName_${idx}`]}</div>}
                  </FormGroup>
                </div>

                {/* Email & Type */}
                <div className="inv-person-grid-2" style={{ marginBottom: 12 }}>
                  <FormGroup className="inv-fg">
                    <Label className="inv-label">Email <span className="inv-req">*</span></Label>
                    <Input
                      className={`inv-input${formErrors[`email_${idx}`] ? ' error' : ''}`}
                      value={person.email || ''}
                      onChange={(e) => updatePerson(idx, 'email', e.target.value)}
                      placeholder="name@example.com"
                    />
                    {formErrors[`email_${idx}`] && <div className="inv-err">{formErrors[`email_${idx}`]}</div>}
                  </FormGroup>

                  <FormGroup className="inv-fg">
                    <Label className="inv-label">Type <span className="inv-req">*</span></Label>
                    <Select
                      options={personTypes}
                      value={personTypes.find((o) => o.value === person.type) || null}
                      onChange={(opt) => {
                        updatePerson(idx, 'type', opt?.value || '');
                        if (opt?.value !== 'other') clearError(`specifyType_${idx}`);
                      }}
                      placeholder="Select type…"
                      styles={selectCustomStyles(!!formErrors[`type_${idx}`])}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                    />
                    {formErrors[`type_${idx}`] && <div className="inv-err">{formErrors[`type_${idx}`]}</div>}
                  </FormGroup>
                </div>

                {/* Specify Type */}
                {person.type === 'other' && (
                  <div style={{ marginBottom: 12 }}>
                    <FormGroup className="inv-fg">
                      <Label className="inv-label">Specify Type <span className="inv-req">*</span></Label>
                      <Input
                        className={`inv-input${formErrors[`specifyType_${idx}`] ? ' error' : ''}`}
                        value={person.specifyType || ''}
                        onChange={(e) => updatePerson(idx, 'specifyType', e.target.value)}
                        placeholder="e.g. Visitor, Vendor, Guest…"
                      />
                      {formErrors[`specifyType_${idx}`] ? (
                        <div className="inv-err">{formErrors[`specifyType_${idx}`]}</div>
                      ) : (
                        <div className="inv-specify-type-note">Describe the person's relationship to the property</div>
                      )}
                    </FormGroup>
                  </div>
                )}

                {person.isConfirmed && (
                  <div style={{ fontSize: 11, color: '#00895e', marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FeatherIcon icon="check-circle" size={12} />
                    Confirmed on {new Date(person.confirmedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}

            <button type="button" className="inv-add-btn" onClick={addPerson} disabled={isSubmitting}>
              <FeatherIcon icon="plus" size={14} /> Add another recipient
            </button>

            {formErrors.global && (
              <div className="inv-global-err" style={{ marginTop: 16 }}>
                <FeatherIcon icon="alert-circle" size={14} />
                {formErrors.global}
              </div>
            )}
          </div>

          {/* Expiry & Message */}
          <div className="inv-bottom-grid">
            <div>
              <div className="inv-sec-head">
                <span className="inv-sec-label">Link Expires In</span>
                <div className="inv-sec-rule" />
              </div>
              <div className="inv-chips">
                {QUICK_DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`inv-chip ${expiresInDays === d ? 'active' : ''}`}
                    onClick={() => setExpiresInDays(d)}
                  >
                    {d} days
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="inv-sec-head">
                <span className="inv-sec-label">Message to Recipients</span>
                <div className="inv-sec-rule" />
              </div>
              <Input
                type="textarea"
                className="inv-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional instructions or greeting…"
                rows={8}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="inv-footer">
          <button type="button" className="inv-cancel" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </button>

          <button type="button" className="inv-send" onClick={handleSubmit} disabled={isSubmitting}>
            <FeatherIcon
              icon={isEditMode ? 'refresh-cw' : 'send'}
              size={15}
              className={isSubmitting ? 'animate-spin' : ''}
            />
            {isSubmitting
              ? 'Sending...'
              : isEditMode
                ? 'Resend Invitation'
                : 'Send Invitation'}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { GenerateIncidentInvitationModal };